import { NumberRangeFilterDef } from '@tanstack/react-table';
import { Trans, useTranslation } from '@sib/i18n';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Slider,
  cn,
} from '@sib/ui';
import { X } from '@sib/ui/icons';
import { RangeInputField } from './RangeInputField';
import { useNumberRangeFilter } from './NumberRangeFilter.utils';

interface NumberRangeFilterProps {
  value: string[];
  onChange?: (from: string, to: string) => void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  defaultFrom?: number;
  defaultTo?: number;
  unit?: string;
  filterProps?: NumberRangeFilterDef;
  size?: number;
  showSlider?: boolean;
}

export const NumberRangeFilter = ({
  value,
  onChange,
  onClear,
  placeholder,
  disabled = false,
  min = 0,
  max = 100,
  defaultFrom,
  defaultTo,
  unit,
  filterProps,
  size = 280,
  showSlider = false,
}: NumberRangeFilterProps) => {
  const { t } = useTranslation();

  const {
    from,
    to,
    hasAppliedRange,
    isDefaultRange,
    buttonLabel,
    sliderValue,
    open,
    setOpen,
    handleSliderChange,
    handleFromInputChange,
    handleToInputChange,
    handleClear,
    handleConfirm,
    formatWithUnit,
  } = useNumberRangeFilter(value, min, max, {
    defaultFrom,
    defaultTo,
    unit,
    placeholder,
    onChange,
    onClear,
  });
  const { TriggerProps, ContentProps } = filterProps?.PopoverProps ?? {};

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="custom"
          variant="outline"
          aria-expanded={open}
          className={cn(
            'hover:text-primary relative h-[30px] w-full justify-between overflow-hidden border-0 px-2 py-1 hover:bg-white',
            TriggerProps?.className,
            disabled && 'cursor-not-allowed opacity-60'
          )}
          disabled={disabled}
          onClick={e => e.stopPropagation()}
          {...TriggerProps}
        >
          <span className="truncate">{buttonLabel}</span>
          {hasAppliedRange && (
            <span
              className="shrink-0 cursor-pointer"
              onClick={e => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <X className="size-4" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-full p-0', ContentProps?.className)}
        style={{ width: size }}
        align="center"
        onClick={e => e.stopPropagation()}
        {...ContentProps}
      >
        <div className="flex flex-col gap-4 p-4">
          <h4 className="text-primary text-sm font-medium">
            <Trans
              i18nKey="components.numberRangeFilter.selectRange"
              values={{ unit: unit ? `(${unit})` : '' }}
              components={{ muted: <span className="text-muted-foreground" /> }}
            />
          </h4>
          {showSlider && (
            <div className="flex flex-col gap-2">
              <Slider
                className="mt-0.5"
                min={min}
                max={max}
                value={sliderValue}
                onValueChange={handleSliderChange}
                ThumbProps={{
                  className:
                    'focus-visible:outline-none bg-secondary size-3 border-2 border-secondary',
                }}
                TrackProps={{
                  className: 'h-1 bg-muted-foreground',
                }}
                WrapperProps={{
                  className: 'mb-3',
                }}
                RangeProps={{
                  className: 'bg-secondary/70',
                }}
                withEdgeValues
                valueFormatter={value => formatWithUnit(value ?? 0)}
              />
            </div>
          )}
          <div className="flex w-full justify-between gap-2">
            <RangeInputField
              label={t('from')}
              value={from}
              min={min}
              max={max}
              onChange={handleFromInputChange}
            />
            <RangeInputField
              label={t('to')}
              value={to}
              min={min}
              max={max}
              onChange={handleToInputChange}
            />
          </div>
          <div className="flex items-center justify-center gap-2 border-t pt-3">
            <Button
              disabled={isDefaultRange}
              variant="outline"
              onClick={handleClear}
            >
              {t('clear')}
            </Button>
            <Button disabled={isDefaultRange} onClick={handleConfirm}>
              {t('confirm')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
